/**
 * UserBrowserAdapter - Concrete adapter implementation for user/member browsing
 *
 * This adapter provides user-specific functionality for the universal selector
 * architecture. It handles organization membership data, team filtering,
 * archived users, and the optional "No User" selection.
 *
 * Key Features:
 * - Fetches users from organization membership cache
 * - Filters by name, email, and team membership
 * - Supports "No User" option for clearing assignments
 * - Handles archived/inactive user filtering
 * - Integrates with existing user stores and queries
 *
 * @example
 * ```typescript
 * const adapter = new UserBrowserAdapter(
 *   {
 *     getId: (member) => member.userId,
 *     getDisplayName: (member) => member.user?.name || member.user?.email || 'Unknown',
 *   },
 *   {
 *     mode: 'single',
 *     includeArchived: false,
 *   }
 * );
 * await adapter.initialize();
 * ```
 */

import { BaseBrowserAdapter } from './BaseBrowserAdapter';
import type { IItemConfig, BrowserContext } from '../types/browser-interfaces';
import {
  type OrganizationMembership,
  type OrganizationMembershipQuery,
  OrganizationMembershipStatus,
  OrganizationMembershipRole,
} from '@/models/user.models';
import { useOrgMembershipsQuery } from '@/utils/users';
import { organizationData } from '@/store/helpers';
import { ref, type Ref } from 'vue';
import type { UserFilters } from '../types/filter-types';

export interface UserBrowserContext extends BrowserContext {
  teamIds?: string[];
  noUserOption?: boolean;
  noUserText?: string;
  currentUserId?: string;
}

export class UserBrowserAdapter extends BaseBrowserAdapter<OrganizationMembership> {
  // Extended context for user-specific options
  protected userContext: UserBrowserContext;

  // Query ref for reactive filtering
  private queryRef: Ref<OrganizationMembershipQuery>;

  // Store references
  private orgMembershipCache: Ref<OrganizationMembership[]> | null = null;

  // Special "No User" handling
  private includeNoUserOption: boolean = false;
  private noUserText: string = 'No User';
  private noUserSelected: boolean = false;

  constructor(
    itemConfig: IItemConfig<OrganizationMembership>,
    initialContext?: Partial<UserBrowserContext>
  ) {
    // Set default item config with user-specific overrides
    const defaultConfig: IItemConfig<OrganizationMembership> = {
      getId: itemConfig.getId || ((member) => member.userId),
      getDisplayName:
        itemConfig.getDisplayName ||
        ((member) => member.user?.name || member.user?.email || 'Unknown User'),
      getSecondaryText:
        itemConfig.getSecondaryText || ((member) => member.user?.email || ''),
      getIcon: itemConfig.getIcon || (() => 'person'),
      isArchived:
        itemConfig.isArchived ||
        ((member) => member.status === OrganizationMembershipStatus.INACTIVE),
      isSelectable: itemConfig.isSelectable || (() => true),
      isDisabled: itemConfig.isDisabled || (() => false),
      getTooltip:
        itemConfig.getTooltip ||
        ((member) => {
          const teams = member.teams?.join(', ') || 'No teams';
          return `${member.user?.email} - Teams: ${teams}`;
        }),
    };

    super(defaultConfig, initialContext);

    // Initialize user-specific context
    this.userContext = {
      ...this.context,
      teamIds: initialContext?.teamIds,
      noUserOption: initialContext?.noUserOption || false,
      noUserText: initialContext?.noUserText || 'No User',
      currentUserId: initialContext?.currentUserId,
    };

    this.includeNoUserOption = this.userContext.noUserOption || false;
    this.noUserText = this.userContext.noUserText || 'No User';

    // Initialize query ref for reactive filtering
    this.queryRef = ref<OrganizationMembershipQuery>({});
  }

  /**
   * Initialize the browser with user data
   */
  async initialize(context?: UserBrowserContext): Promise<void> {
    if (context) {
      this.userContext = { ...this.userContext, ...context };
      this.context = { ...this.context, ...context };
      this.selectionMode = context.mode || this.selectionMode;
    }

    // Get organization data
    const { orgMembershipCache } = organizationData();
    this.orgMembershipCache = orgMembershipCache;

    // Build query based on context
    this.updateQuery();

    // Load items using parent initialization
    await super.initialize(this.context);
  }

  /**
   * Load users from the organization membership cache
   */
  protected async loadItems(): Promise<OrganizationMembership[]> {
    // Use the reactive query to get filtered members
    const { filteredCachedMembers } = useOrgMembershipsQuery({
      query: this.queryRef,
    });

    // Get the current value of the computed ref
    const members = filteredCachedMembers.value || [];

    // Return the filtered members
    return members;
  }

  /**
   * Filter users based on search query
   * Enhanced to search in name, email, and teams
   */
  protected filterItem(member: OrganizationMembership, query: string): boolean {
    const searchLower = query.toLowerCase();

    // Check user name
    if (member.user?.name?.toLowerCase().includes(searchLower)) {
      return true;
    }

    // Check user email
    if (member.user?.email?.toLowerCase().includes(searchLower)) {
      return true;
    }

    // Check team names if available
    if (member.teams && member.teams.length > 0) {
      // In a real implementation, you'd need to resolve team names from IDs
      // For now, just check if any team ID contains the search
      return member.teams.some((teamId) =>
        teamId.toLowerCase().includes(searchLower)
      );
    }

    // Check role
    if (member.role?.toLowerCase().includes(searchLower)) {
      return true;
    }

    return false;
  }

  /**
   * Sort users alphabetically by name, then by email
   */
  protected sortItems(
    members: OrganizationMembership[]
  ): OrganizationMembership[] {
    return members.sort((a, b) => {
      // First sort by name
      const aName = (a.user?.name || '').toLowerCase();
      const bName = (b.user?.name || '').toLowerCase();

      if (aName !== bName) {
        return aName.localeCompare(bName);
      }

      // Then by email as secondary sort
      const aEmail = (a.user?.email || '').toLowerCase();
      const bEmail = (b.user?.email || '').toLowerCase();
      return aEmail.localeCompare(bEmail);
    });
  }

  /**
   * Apply user-specific filters
   */
  protected applyCustomFilters(
    members: OrganizationMembership[],
    filters: UserFilters
  ): OrganizationMembership[] {
    let filtered = [...members];

    // Filter by team IDs if specified
    if (this.userContext.teamIds && this.userContext.teamIds.length > 0) {
      filtered = filtered.filter((member) => {
        if (!member.teams || member.teams.length === 0) return false;
        // Check if user belongs to at least one of the specified teams
        return member.teams.some((teamId) =>
          this.userContext.teamIds!.includes(teamId)
        );
      });
    }

    // Filter by additional custom filters
    if (filters.role && typeof filters.role === 'string') {
      filtered = filtered.filter((member) => member.role === filters.role);
    }

    if (
      filters.labels &&
      Array.isArray(filters.labels) &&
      filters.labels.length > 0
    ) {
      filtered = filtered.filter((member) =>
        member.labels?.some((label) =>
          (filters.labels as string[]).includes(label)
        )
      );
    }

    // Filter out current user if specified
    if (this.userContext.currentUserId) {
      filtered = filtered.filter(
        (member) => member.userId !== this.userContext.currentUserId
      );
    }

    return filtered;
  }

  /**
   * Update the query ref based on current context
   */
  private updateQuery(): void {
    const query: OrganizationMembershipQuery = {
      q: this.context.searchQuery,
      teamId: this.userContext.teamIds,
    };

    // Handle archived/inactive users
    if (this.context.includeArchived) {
      query.status = [
        OrganizationMembershipStatus.ACTIVE,
        OrganizationMembershipStatus.INVITED,
        OrganizationMembershipStatus.IMPORTED,
        OrganizationMembershipStatus.INACTIVE,
      ];
    } else {
      query.status = [
        OrganizationMembershipStatus.ACTIVE,
        OrganizationMembershipStatus.INVITED,
        OrganizationMembershipStatus.IMPORTED,
      ];
    }

    // Apply additional filters
    if (
      this.context.filters?.role &&
      typeof this.context.filters.role === 'string'
    ) {
      query.role = [this.context.filters.role as OrganizationMembershipRole]; // UserFilters.role is string, query.role expects OrganizationMembershipRole[]
    }

    if (
      this.context.filters?.labels &&
      Array.isArray(this.context.filters.labels)
    ) {
      query.label = this.context.filters.labels as string[]; // Type narrow to string[]
    }

    this.queryRef.value = query;
  }

  /**
   * Override setSearchQuery to update the reactive query
   */
  setSearchQuery(query: string): void {
    super.setSearchQuery(query);
    this.updateQuery();
  }

  /**
   * Get items including the "No User" option if enabled
   */
  getItems(): OrganizationMembership[] {
    const items = super.getItems();

    // Add "No User" option at the beginning if enabled and in single mode
    if (
      this.includeNoUserOption &&
      this.selectionMode === 'single' &&
      !this.state.searchQuery // Don't show during search
    ) {
      // Create a pseudo-member for "No User"
      // Use type assertion to create a special OrganizationMembership for this case
      const noUserOption = {
        userId: '__no_user__',
        user: {
          id: '__no_user__',
          name: this.noUserText,
          email: '',
        },
        organizationId: '',
        role: '',
        status: OrganizationMembershipStatus.ACTIVE,
        teams: [],
        labels: [],
      } as unknown as OrganizationMembership;

      return [noUserOption, ...items];
    }

    return items;
  }

  /**
   * Handle "No User" selection
   */
  selectNoUser(): void {
    if (this.selectionMode === 'single' && this.includeNoUserOption) {
      this.noUserSelected = true;
      this.state.selectedItems = [];
      this.state.focusedItem = null;
    }
  }

  /**
   * Check if "No User" is selected
   */
  isNoUserSelected(): boolean {
    return this.noUserSelected;
  }

  /**
   * Override selectItem to handle "No User" option
   */
  selectItem(item: OrganizationMembership): void {
    // Check if this is the "No User" option
    if (item.userId === '__no_user__') {
      this.selectNoUser();
      return;
    }

    // Clear "No User" selection when selecting a real user
    this.noUserSelected = false;

    super.selectItem(item);
  }

  /**
   * Override clearSelection to also clear "No User" selection
   */
  clearSelection(): void {
    super.clearSelection();
    this.noUserSelected = false;
  }

  /**
   * Set archived users inclusion
   */
  async setIncludeArchived(include: boolean): Promise<void> {
    this.context.includeArchived = include;
    this.updateQuery();
    // Reload items from source with updated query
    await this.refreshItemsForCurrentContext();
  }

  /**
   * Set team filter
   */
  async setTeamFilter(teamIds: string[]): Promise<void> {
    this.userContext.teamIds = teamIds;
    this.updateQuery();
    // Reload items from source with updated query
    await this.refreshItemsForCurrentContext();
  }

  /**
   * Check if there are more users to load
   * Users are typically loaded all at once from cache, so no pagination
   */
  protected async checkHasMore(): Promise<boolean> {
    // Organization membership cache is loaded completely, no pagination
    return false;
  }

  /**
   * Get display metadata for a user
   */
  getUserMetadata(member: OrganizationMembership): {
    avatar?: string;
    initials: string;
    color: string;
  } {
    const name = member.user?.name || member.user?.email || 'Unknown';
    const initials = name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);

    // Generate a consistent color based on user ID
    const colors = [
      '#3B82F6',
      '#10B981',
      '#F59E0B',
      '#EF4444',
      '#8B5CF6',
      '#EC4899',
      '#14B8A6',
      '#F97316',
    ];
    const colorIndex = member.userId.charCodeAt(0) % colors.length;
    const color = colors[colorIndex];

    return {
      avatar: member.user?.avatarUrl,
      initials,
      color,
    };
  }
}
